import "./AccountTransactionsComonent.scss";
import template from "./AccountTransactionsComponent.html";
import {IComponentOptions, IOnDestroy} from "angular";
import {Observable, ReplaySubject} from "rxjs";
import {IsoDate} from "../../../pseudo-types/DateTypes";
import {DateTimeUtil} from "../../../js/util/DateTimeUtil";
import moment from "moment";
import {AccountTransactionRecord, AccountTransactionRecords} from "../../types/AccountTransactionRecord";
import {AccountTransactionsService} from "../../services/AccountTransactionsService";
import {ArrayUtil} from "../../../js/util/ArrayUtil";
import {descendingUsing} from "../../../js/util/UtilFunctions";
import {AccountId} from "../../types/AccountRecord";
import FileSaver from "file-saver";

export const AccountTransactions = "tfAccountTransactions";

export function accountTransactions(): IComponentOptions {
    return {
        template,
        bindings: {
            accountId: "<"
        },
        controllerAs: "ctrl",
        controller: AccountTransactionsComponentController
    };
}

export class AccountTransactionsComponentController implements IOnDestroy {

    /** @input */
    public set accountId(accountId: AccountId | null) {
        if (accountId != null) {
            this.accountId$.next(accountId);
        }
    }

    private readonly accountId$ = new ReplaySubject<AccountId>(1);

    public readonly toDate$ = new ReplaySubject<IsoDate>();
    public readonly fromDate$ = new ReplaySubject<IsoDate>();

    public readonly transactions$: Observable<AccountTransactionRecords>;

    private readonly export$ = new ReplaySubject<boolean>(1);
    private readonly destroy$ = new ReplaySubject<boolean>(1);

    /** @ngInject */
    public constructor(private readonly accountTransactionsService: AccountTransactionsService) {
        this.initializeDates();
        this.transactions$ = this.buildTransactions();

        this.export$
            .withLatestFrom(this.transactions$)
            .takeUntil(this.destroy$)
            .subscribe(([_, transactions]) => {
                this.exportLedgerTransactionsToCsv(transactions, "account transactions.csv");
            });
    }

    private initializeDates() {
        const toDate = DateTimeUtil.momentToIsoDate(moment());
        this.toDate$.next(toDate);
        const from = moment().subtract(1, "year");
        const fromDate = DateTimeUtil.momentToIsoDate(from);
        this.fromDate$.next(fromDate);
    }

    private buildTransactions(): Observable<AccountTransactionRecords> {
        const source$ = Observable.combineLatest(this.accountId$, this.fromDate$, this.toDate$);

        return source$.switchMap(([acc, fromDate, toDate]) => this.retrieveTransactions(acc, fromDate, toDate))
                      .map((transactions) => ArrayUtil.clone(transactions).sort(descendingUsing((a) => a.ledgerDate)))
                      .publishReplay(1)
                      .refCount();
    }

    private retrieveTransactions(accountId: AccountId,
                                 fromDate: IsoDate,
                                 toDate: IsoDate): Observable<AccountTransactionRecords> {
        const response = this.accountTransactionsService.list(accountId, fromDate, toDate);
        return Observable.fromPromise(response)
                         .catch(() => Observable.empty());
    }

    public updateToDate(date: IsoDate | null) {
        if (date != null) {
            this.toDate$.next(date);
        }
    }

    public updateFromDate(date: IsoDate | null) {
        if (date != null) {
            this.fromDate$.next(date);
        }
    }

    public exportToCsv() {
        this.export$.next(true);
    }

    private exportLedgerTransactionsToCsv(transactions: AccountTransactionRecords, fileName: string) {
        const header = `Date,Settlement Date,Code,Reference,Description,Status,Outstanding,Amount,Balance`;
        const body = transactions.map((model) => this.formatAsCsv(model))
                           .join("\n");
        const csv = header + "\n" + body;

        const options = {type: "text/csv"};
        const blob = new Blob([csv], options);
        FileSaver.saveAs(blob, fileName);
    }

    private formatAsCsv(transaction: AccountTransactionRecord) {
        const ledgerDate = transaction.ledgerDate;
        const settleDate = transaction.settlementDate;
        const code = transaction.transactionCode;
        const ref = transaction.reference;
        const description = transaction.description;
        const status = transaction.status;
        const outstanding = transaction.outstandingAmount;
        const amount = transaction.amount;
        const balance = transaction.runningBalance;

        return [ledgerDate, settleDate, code, ref, description, status, outstanding, amount, balance].join(",");
    }

    public $onDestroy() {
        this.destroy$.next(true);
        this.destroy$.complete();
    }
}
